import { Input } from '@kalos/ui';
import { Loader2, MapPin } from 'lucide-react';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { useLoadGoogleMapsPlaces } from '../hooks/useLoadGoogleMapsPlaces';

export interface SelectedAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
}

interface Props
  extends Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'onSelect'> {
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: SelectedAddress) => void;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY = 3;

const parseComponents = (components: google.maps.GeocoderAddressComponent[]): SelectedAddress => {
  const get = (type: string, short = false) => {
    const c = components.find((comp) => comp.types.includes(type));
    return c ? (short ? c.short_name : c.long_name) : '';
  };
  return {
    address: [get('street_number'), get('route')].filter(Boolean).join(' '),
    city:
      get('locality') ||
      get('sublocality') ||
      get('postal_town') ||
      get('administrative_area_level_3'),
    state: get('administrative_area_level_1', true),
    zip: get('postal_code'),
  };
};

/**
 * Address search backed by the Google Places predictions API with a custom-rendered
 * dropdown. Controlled: `onChange` receives raw typing (manual fallback), `onSelect`
 * fires with the parsed address when a suggestion is picked.
 */
export const AddressSearchInput: FC<Props> = ({
  value,
  onChange,
  onSelect,
  placeholder,
  autoFocus,
  ...rest
}) => {
  const placesLib = useLoadGoogleMapsPlaces();
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const detailsRef = useRef<google.maps.places.PlacesService | null>(null);
  const tokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!placesLib) return;
    serviceRef.current = new placesLib.AutocompleteService();
    detailsRef.current = new placesLib.PlacesService(document.createElement('div'));
    tokenRef.current = new placesLib.AutocompleteSessionToken();
  }, [placesLib]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const runSearch = useCallback((input: string) => {
    const service = serviceRef.current;
    if (!service || input.trim().length < MIN_QUERY) {
      setPredictions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    service.getPlacePredictions(
      {
        input,
        types: ['address'],
        componentRestrictions: { country: 'us' },
        sessionToken: tokenRef.current ?? undefined,
      },
      (results) => {
        setPredictions(results ?? []);
        setOpen(true);
        setLoading(false);
      },
    );
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      onChange(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => runSearch(next), DEBOUNCE_MS);
    },
    [onChange, runSearch],
  );

  const handlePick = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      const details = detailsRef.current;
      if (!details) return;
      details.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['address_components'],
          sessionToken: tokenRef.current ?? undefined,
        },
        (place) => {
          if (place?.address_components) {
            onSelect(parseComponents(place.address_components));
          }
          if (placesLib) tokenRef.current = new placesLib.AutocompleteSessionToken();
          setPredictions([]);
          setOpen(false);
        },
      );
    },
    [onSelect, placesLib],
  );

  return (
    <div className="relative">
      <Input
        {...rest}
        value={value}
        onChange={handleChange}
        onFocus={() => predictions.length > 0 && setOpen(true)}
        placeholder={placeholder ?? 'Start typing an address…'}
        autoFocus={autoFocus}
        autoComplete="off"
      />
      {loading && (
        <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
      )}
      {open && predictions.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {predictions.map((p) => (
            <button
              key={p.place_id}
              type="button"
              className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handlePick(p)}
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900">
                  {p.structured_formatting?.main_text ?? p.description}
                </span>
                {p.structured_formatting?.secondary_text && (
                  <span className="block truncate text-xs text-gray-500">
                    {p.structured_formatting.secondary_text}
                  </span>
                )}
              </span>
            </button>
          ))}
          <div className="flex justify-end px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400">
            powered by Google
          </div>
        </div>
      )}
    </div>
  );
};
